<template>
  <v-card
    :class="['recommendation-card', isHovered ? 'elevation-2' : 'elevation-0']"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @click="handleClick">
    <div class="recommendation-card__heading">
      <img
        :src="recommendation.arena_promotion.image"
        class="recommendation-card__image" />
      <h3 class="recommendation-card__name">
        {{ recommendation.arena_promotion.name }}
      </h3>
    </div>
    <div class="recommendation-card__price-section">
      <p class="recommendation-card__price-discounted">
        {{ formatPrice(recommendation.price_with_discount) }}
      </p>
      <p class="recommendation-card__price-original">
        {{ formatPrice(recommendation.price_without_discount) }}
      </p>
      <p class="recommendation-card__discount-amount">
        -{{ getDiscountPct }} %
      </p>
    </div>
    <div
      v-if="missingItemNames.length"
      class="recommendation-card__missing-groups">
      <h4 class="recommendation-card__missing-groups-label">
        {{ $t('miscellaneous.add') }}:
      </h4>
      <ul class="recommendation-card__missing-groups-list">
        <li
          v-for="(itemName, index) in missingItemNames"
          :key="index"
          class="recommendation-card__missing-groups-item">
          {{ itemName }}
        </li>
      </ul>
    </div>
  </v-card>
</template>

<script>
import { convertNativeJsToFriendlyFireWeekdayIndex } from '@/utils/weekdays'
import {
  formatPrice as formatPriceUtil,
  getReductionPercentage,
} from '@/utils/price'
import { isFixedPricePromotion } from '@/utils/promotions'

export default {
  name: 'RecommendationCard',
  props: {
    recommendation: { type: Object, required: true },
    arenaCurrency: { type: Object, default: () => ({}) },
  },
  data: () => ({
    isHovered: false,
  }),
  computed: {
    isPassAvailableToday() {
      return this.passMissingItems.some((missingItem) => {
        const weekdays = missingItem.arena_variant.attributes.weekdays
        const jsWeekdayIndex = new Date().getDay()
        const dayIdx = convertNativeJsToFriendlyFireWeekdayIndex(jsWeekdayIndex)
        return weekdays.includes(dayIdx)
      })
    },
    doesRecommendationContainPasses() {
      return this.passMissingItems?.length
    },
    missingItemNames() {
      if (!this.recommendation?.missing_items?.length) return []
      return this.recommendation.missing_items.map((missingItem) => {
        const quantity = missingItem.quantity
        const itemName = missingItem.arena_variant.name
        return `${quantity}x ${itemName}`
      })
    },
    passMissingItems() {
      return this.recommendation.missing_items.filter(
        (it) => it.arena_variant.attributes?.weekdays?.length,
      )
    },
    getDiscountPct() {
      if (isFixedPricePromotion(this.recommendation.arena_promotion)) {
        return getReductionPercentage(
          this.recommendation.price_without_discount,
          this.recommendation.price_with_discount,
        )
      }
      return this.recommendation.arena_promotion.discount_value
    },
  },
  methods: {
    formatPrice(price) {
      const currency =
        this.arenaCurrency?.symbol || this.arenaCurrency?.code || ''
      return `${formatPriceUtil(price)} ${currency}`
    },
    handleMouseEnter() {
      this.isHovered = true
      this.$emit('hover-enter', this.recommendation)
    },
    handleMouseLeave() {
      this.isHovered = false
      this.$emit('hover-leave')
    },
    handleClick() {
      if (this.doesRecommendationContainPasses && !this.isPassAvailableToday) {
        this.$store.dispatch('showSnackbar', {
          color: 'error',
          message: this.$t('purchaseV2.basket.recommendationNotAvailableToday'),
          icon: 'error',
        })
        return
      }
      this.$emit('click', this.recommendation)
    },
  },
}
</script>

<style scoped lang="scss">
.recommendation-card {
  --recommendation-card-bg: #284b7633;
  --recommendation-card-text: #312929;
  --recommendation-card-discount: #146614;
  border-radius: 8px;
  padding: 0.5rem;
  background-color: var(--recommendation-card-bg) !important;
  transition: box-shadow 0.3s ease;
  cursor: pointer;

  &:last-child {
    margin-bottom: 4px;
  }
}

.theme--dark .recommendation-card {
  --recommendation-card-bg: rgba(40, 75, 118, 0.35);
  --recommendation-card-text: rgba(255, 255, 255, 0.87);
  --recommendation-card-discount: #81c784;
}

.recommendation-card__heading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.recommendation-card__image {
  width: 2.5rem;
  height: 2.5rem;
  object-fit: cover;
  border-radius: 4px;
  flex-shrink: 0;
}

.recommendation-card__name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--recommendation-card-text);
  flex: 1;
}

.recommendation-card__price-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.5rem;
}

.recommendation-card__price-discounted {
  color: var(--recommendation-card-text);
  font-weight: 700;
  font-size: 18px;
  margin: 0;
  word-spacing: -0.15em;
}

.recommendation-card__price-original {
  color: var(--recommendation-card-text);
  font-weight: 500;
  font-size: 14px;
  margin: 0;
  position: relative;
  display: inline-block;
  word-spacing: -0.15em;
}

.recommendation-card__price-original::before {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 110%;
  height: 2px;
  background-color: red;
  transform: translateY(-50%) rotate(-18deg);
  transform-origin: bottom left;
}

.recommendation-card__discount-amount {
  color: var(--recommendation-card-discount);
  font-weight: 700;
  font-size: 18px;
  margin: 0;
  word-spacing: -0.15em;
}

.recommendation-card__missing-groups {
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: var(--recommendation-card-text);
}

.recommendation-card__missing-groups-list {
  margin: 0;
}

.recommendation-card__missing-groups-item {
  margin: 0.125rem 0;
}
</style>
